import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { enqueue, flush, isNetworkError, isQueueable, newIdempotencyKey } from './offlineQueue';
import { viewModeHeader } from './viewMode';
import { logClient } from './logger';

// Per-request timing marker, stamped in the request interceptor and read back in
// the response/error handlers to compute round-trip duration.
type TimedConfig = InternalAxiosRequestConfig & { _startedAt?: number };

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  withCredentials: false,
  timeout: 30_000,
});

const ACCESS_TOKEN_KEY = 'kalachand.accessToken';
const REFRESH_TOKEN_KEY = 'kalachand.refreshToken';

export function setTokens(access: string | null, refresh: string | null): void {
  if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
  if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  else localStorage.removeItem(REFRESH_TOKEN_KEY);
}
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

// ── Request interceptor: auth token + idempotency key for writes ───────────
api.interceptors.request.use((cfg) => {
  (cfg as TimedConfig)._startedAt = performance.now();
  const token = getAccessToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  // Sandbox⇄live read scoping for test users. Real users' value is ignored
  // server-side (they're always forced to live data).
  cfg.headers['X-View-Mode'] = viewModeHeader();
  // Tag every queueable write with an idempotency key so server-side dedup
  // works for any replay (online retry or offline-queue flush).
  if (isQueueable(cfg) && !cfg.headers['Idempotency-Key']) {
    cfg.headers['Idempotency-Key'] = newIdempotencyKey();
  }
  return cfg;
});

// ── Silent token refresh ────────────────────────────────────────────────────
// Bare client (no interceptors) so a failing refresh can't recurse into itself.
// Concurrent 401s share one in-flight refresh via `refreshPromise`, so a page
// that fires several queries at once only triggers a single /auth/refresh call.
const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  timeout: 30_000,
});
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const res = await refreshClient.post<{ data: { accessToken: string; refreshToken: string } }>(
      '/auth/refresh',
      { refreshToken: refresh },
    );
    setTokens(res.data.data.accessToken, res.data.data.refreshToken);
    return res.data.data.accessToken;
  } catch {
    setTokens(null, null);
    return null;
  }
}

function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Round-trip duration for a completed request, in whole milliseconds.
function durationOf(cfg?: TimedConfig): number | undefined {
  const start = cfg?._startedAt;
  return typeof start === 'number' ? Math.round(performance.now() - start) : undefined;
}

// ── Response interceptor: 401 → refresh+retry (else logout), network → queue ─
api.interceptors.response.use(
  (res) => {
    // Success: record src (method+url), status, duration, and the correlation
    // id echoed by the backend so this line ties to a server log line.
    const cfg = res.config as TimedConfig;
    logClient({
      level: 'info',
      method: cfg.method?.toUpperCase(),
      url: cfg.url,
      status: res.status,
      durationMs: durationOf(cfg),
      requestId: (res.headers?.['x-request-id'] as string) || undefined,
    });
    return res;
  },
  async (err: AxiosError) => {
    // Failure: log before any retry/queue branching. Network errors have no
    // response → status is left undefined and the level is bumped to error.
    const failCfg = err.config as TimedConfig | undefined;
    const failStatus = err.response?.status;
    logClient({
      level: failStatus && failStatus < 500 ? 'warn' : 'error',
      method: failCfg?.method?.toUpperCase(),
      url: failCfg?.url,
      status: failStatus,
      durationMs: durationOf(failCfg),
      requestId: (err.response?.headers?.['x-request-id'] as string) || undefined,
      msg: err.message,
    });
    // Blob-typed requests (file downloads: PDF/CSV/XLSX) keep responseType 'blob'
    // even on error, so the server's JSON error body arrives as an opaque Blob
    // instead of parsed JSON. Unwrap it here so getApiError() can read the real
    // message instead of falling back to axios's generic "Request failed with
    // status code NNN".
    if (err.response?.data instanceof Blob && err.response.data.type.includes('json')) {
      try {
        err.response.data = JSON.parse(await err.response.data.text());
      } catch {
        // Not actually JSON — leave as-is, getApiError() falls back gracefully.
      }
    }

    const original = err.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    // Access token likely expired → transparently refresh once and replay.
    if (err.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      // No usable refresh token (or refresh rejected) → genuinely signed out.
      window.dispatchEvent(new CustomEvent('kalachand:unauthorized'));
      return Promise.reject(err);
    }
    // Already retried once (or no config) and still 401 → give up.
    if (err.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('kalachand:unauthorized'));
    }
    // Device-trust gate: authenticated but not on a company device (and no
    // remote_access). Surface a dedicated takeover screen, not a generic error.
    if (
      err.response?.status === 403 &&
      typeof err.response.data === 'object' &&
      err.response.data !== null &&
      'error' in err.response.data &&
      (err.response.data as { error?: { code?: string } }).error?.code === 'DEVICE_NOT_TRUSTED'
    ) {
      window.dispatchEvent(new CustomEvent('kalachand:device-untrusted'));
    }
    if (isNetworkError(err)) {
      const cfg = err.config as InternalAxiosRequestConfig | undefined;
      if (cfg && isQueueable(cfg) && !(cfg as { __queued?: boolean }).__queued) {
        const key = (cfg.headers?.['Idempotency-Key'] as string) ?? newIdempotencyKey();
        await enqueue({
          id: key,
          method: cfg.method!.toUpperCase() as 'POST' | 'PATCH' | 'PUT' | 'DELETE',
          url: cfg.url ?? '',
          body: cfg.data,
          headers: {
            'Idempotency-Key': key,
            ...(cfg.headers?.Authorization ? { Authorization: String(cfg.headers.Authorization) } : {}),
          },
        });
        window.dispatchEvent(new CustomEvent('kalachand:queued', { detail: { id: key } }));
        // Re-throw a tagged error so callers can surface "saved offline" UX.
        const queuedErr = new Error('queued_offline') as Error & { isQueued: true; idempotencyKey: string };
        queuedErr.isQueued = true;
        queuedErr.idempotencyKey = key;
        return Promise.reject(queuedErr);
      }
    }
    return Promise.reject(err);
  },
);

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export function getApiError(err: unknown): ApiErrorPayload {
  if (err instanceof Error && (err as { isQueued?: boolean }).isQueued) {
    return { code: 'queued_offline', message: 'Saved offline — will sync automatically when reconnected.' };
  }
  if (err instanceof AxiosError && err.response?.data && typeof err.response.data === 'object' && 'error' in err.response.data) {
    const payload = (err.response.data as { error: ApiErrorPayload }).error;
    // Surface field-level detail for validation errors instead of the generic
    // "Request validation failed". The backend's Zod error map already produces
    // friendly, field-named sentences (e.g. "Phone is required."), so we just
    // collect and de-duplicate them here.
    if (payload.code === 'validation_error' && payload.details) {
      const d = payload.details as { fieldErrors?: Record<string, string[]>; formErrors?: string[] };
      const parts: string[] = [];
      for (const msgs of Object.values(d.fieldErrors ?? {})) {
        const first = msgs?.[0];
        if (first) parts.push(first);
      }
      if (d.formErrors?.length) parts.push(...d.formErrors);
      const unique = [...new Set(parts.filter(Boolean))];
      if (unique.length) return { ...payload, message: unique.join(' · ') };
    }
    return payload;
  }
  return { code: 'unknown_error', message: err instanceof Error ? err.message : 'Unknown error' };
}

export interface PaginatedResult<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await api.get<{ data: T }>(url, { params });
  return res.data.data;
}
export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const res = await api.post<{ data: T }>(url, body);
  return res.data.data;
}
export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const res = await api.patch<{ data: T }>(url, body);
  return res.data.data;
}
export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const res = await api.put<{ data: T }>(url, body);
  return res.data.data;
}
export async function apiDelete(url: string): Promise<void> {
  await api.delete(url);
}

// ── Queue replay (called on reconnect + boot) ───────────────────────────────
export async function flushOfflineQueue(): Promise<{ ok: number; failed: number }> {
  return flush(async (req) => {
    await api.request({
      method: req.method,
      url: req.url,
      data: req.body,
      headers: { ...req.headers },
      // Mark so the interceptor doesn't re-enqueue if this fails (we just give up).
      __queued: true,
    } as InternalAxiosRequestConfig & { __queued: true });
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void flushOfflineQueue().then(({ ok, failed }) => {
      if (ok > 0 || failed > 0) {
        window.dispatchEvent(new CustomEvent('kalachand:queue-flushed', { detail: { ok, failed } }));
      }
    });
  });
  // Boot replay (covers the case where the tab was closed offline)
  void flushOfflineQueue();
}
